import { PeriodType, StatementType, type SuretyAccountId } from "@inrev/common";
import {
	useAdminCreateBalanceSheet,
	useAdminCreateIncomeStatement,
} from "@portal/api/financials/admin";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useState } from "react";
import { cn } from "../lib/utils";
import { PeriodYearList } from "../patterns/period-selector";
import { SideDrawer, type SideDrawerControlProps } from "../patterns/side-drawer";
import { Icon } from "../primitives";
import { useDataGridExpansionDispatch } from "../ui/DataGrid";
import { HotkeyTag } from "../ui/HotkeyTag";
import { Slider } from "../ui/Slider";
import { CreateOrUpdatePeriodModal, type StatementCreateConfig } from "./CreateOrUpdatePeriodModal";
import { FinancialsPeriodSelectorLabel } from "./FinancialsPeriodSelectorLabel";
import { useFinancialsPeriodSelector } from "./FinancialsPeriodSelectorProvider";
import { PendingExtractionsIndicator } from "./PendingExtractionsIndicator";
import {
	type ActiveStatementType,
	ActiveStatementTypeEnum,
	isAnalysisPeriodSelectorItem,
	isBalanceSheetPeriodSelectorItem,
	isIncomeStatementPeriodSelectorItem,
	type PeriodSelectorItem,
} from "./types";
import { getDefaultPeriodSelectorItems } from "./utils/computeDefaultPeriodSelectorIds";
import { dedupToHighestQualityPeriods } from "./utils/dedupToHighestQualityPeriods";

type PresetOption = "all_fye" | "all_qtd" | "all_ytd" | "all_interim";
type ActivePreset = PresetOption | undefined;

type StatementTabConfig = {
	presets: { option: PresetOption; label: string }[];
};

type PeriodSelectorStatementType = Exclude<ActiveStatementType, "debt_schedule">;

const STATEMENT_TAB_CONFIG: Record<PeriodSelectorStatementType, StatementTabConfig> = {
	[ActiveStatementTypeEnum.BalanceSheet]: {
		presets: [
			{ option: "all_fye", label: "Default" },
			{ option: "all_interim", label: "All Interim" },
		],
	},
	[ActiveStatementTypeEnum.IncomeStatement]: {
		presets: [
			{ option: "all_fye", label: "Default" },
			{ option: "all_qtd", label: "All QTD" },
			{ option: "all_ytd", label: "All YTD" },
		],
	},
	[ActiveStatementTypeEnum.Analysis]: {
		presets: [
			{ option: "all_fye", label: "Default" },
			{ option: "all_qtd", label: "All QTD" },
			{ option: "all_ytd", label: "All YTD" },
		],
	},
};

/**
 * A grid-level toggle rendered in the period selector drawer (e.g. "Show P/P % change"). The
 * drawer is agnostic to where the state lives — each consumer supplies the current `checked` value
 * and an `onToggle` callback, so the drawer can drive the financial statement grids, the analysis
 * grid, or any future DataGrid composition without depending on a particular grid's state.
 */
export type PeriodSelectorTableAction = {
	key: string;
	label: string;
	checked: boolean;
	onToggle: () => void;
	testId: string;
};

const EMPTY_TABLE_ACTIONS: readonly PeriodSelectorTableAction[] = [];

type ToggleRowProps = {
	testId: string;
	label: string;
	checked: boolean;
	onToggle: () => void;
};

const ToggleRow: FC<ToggleRowProps> = ({ testId, label, checked, onToggle }) => (
	<div data-testid={testId} className="flex items-center justify-between gap-8">
		<span className="text-sm text-primary">{label}</span>
		<Slider enabled={checked} onChange={onToggle} />
	</div>
);

type FinancialsPeriodSelectorDrawerPanelProps = {
	accountId: SuretyAccountId;
	expandableRowIds: string[];
	tableActions?: readonly PeriodSelectorTableAction[];
	/** Hide the expand/collapse-all controls. Set for flat grids with no expandable rows. */
	hideExpandControls?: boolean;
};

type FinancialsPeriodSelectorDrawerProps = SideDrawerControlProps &
	FinancialsPeriodSelectorDrawerPanelProps;

export const FinancialsPeriodSelectorDrawer: FC<FinancialsPeriodSelectorDrawerProps> = ({
	isOpen,
	onClose,
	id,
	testId,
	trackingId,
	...panelProps
}) => {
	const { activeStatementType } = useFinancialsPeriodSelector();

	if (activeStatementType === ActiveStatementTypeEnum.DebtSchedule) {
		return null;
	}

	return (
		<SideDrawer
			id={id}
			isOpen={isOpen}
			onClose={onClose}
			title="Periods"
			closeLabel="Close period selector"
			testId={testId}
			trackingId={trackingId}
		>
			<FinancialsPeriodSelectorDrawerPanel {...panelProps} />
		</SideDrawer>
	);
};

const FinancialsPeriodSelectorDrawerPanel: FC<FinancialsPeriodSelectorDrawerPanelProps> = ({
	accountId,
	expandableRowIds,
	tableActions = EMPTY_TABLE_ACTIONS,
	hideExpandControls,
}) => {
	const { isAdmin } = useAuthenticatedUser();
	const { setExpanded } = useDataGridExpansionDispatch();

	const {
		activeStatementType,
		periodSelectorItems,
		setSelectedBalanceSheetIds,
		setSelectedIncomeStatementIds,
		setSelectedFinancialPeriodIds,
		toggleBalanceSheet,
		toggleIncomeStatement,
		toggleFinancialPeriod,
		isBalanceSheetSelected,
		isIncomeStatementSelected,
		isFinancialPeriodSelected,
		selectedCountForTab,
	} = useFinancialsPeriodSelector();

	const { createBalanceSheet, createBSLoading, createBSError, resetCreateBSError } =
		useAdminCreateBalanceSheet(accountId);
	const { createIncomeStatement, createISLoading, createISError, resetCreateISError } =
		useAdminCreateIncomeStatement(accountId);

	const [showCreateModal, setShowCreateModal] = useState(false);

	const createConfig = useMemo<StatementCreateConfig | undefined>(() => {
		if (activeStatementType === ActiveStatementTypeEnum.BalanceSheet) {
			return {
				header: "Add Balance Sheet",
				submitLabel: "Add Balance Sheet",
				isLoading: createBSLoading,
				error: createBSError,
				dismissError: resetCreateBSError,
				submit: ({ periodType, periodEndDate, preparationMethod }, opts) => {
					if (periodType && periodEndDate && preparationMethod) {
						createBalanceSheet(
							{ periodType, statementDate: periodEndDate, preparationMethod },
							opts,
						);
					}
				},
			};
		}
		if (activeStatementType === ActiveStatementTypeEnum.IncomeStatement) {
			return {
				header: "Add Income Statement",
				submitLabel: "Add Income Statement",
				isLoading: createISLoading,
				error: createISError,
				dismissError: resetCreateISError,
				submit: ({ periodType, periodEndDate, preparationMethod }, opts) => {
					if (periodType && periodEndDate && preparationMethod) {
						createIncomeStatement({ periodType, periodEndDate, preparationMethod }, opts);
					}
				},
			};
		}
		return undefined;
	}, [
		activeStatementType,
		createBSLoading,
		createBSError,
		resetCreateBSError,
		createBalanceSheet,
		createISLoading,
		createISError,
		resetCreateISError,
		createIncomeStatement,
	]);

	const isItemSelected = useCallback(
		(item: PeriodSelectorItem) =>
			isBalanceSheetPeriodSelectorItem(item)
				? isBalanceSheetSelected(item.id)
				: isIncomeStatementPeriodSelectorItem(item)
					? isIncomeStatementSelected(item.id)
					: isFinancialPeriodSelected(item.id),
		[isBalanceSheetSelected, isIncomeStatementSelected, isFinancialPeriodSelected],
	);

	// Agents see a deduped list (highest-value period per date); admins see every revision.
	const { presetItems, presetOptions, tabPresets, yearGroups } = useMemo(() => {
		const visibleItems = isAdmin
			? periodSelectorItems
			: dedupToHighestQualityPeriods(periodSelectorItems);

		const presetItems: Record<PresetOption, PeriodSelectorItem[]> = {
			all_fye: getDefaultPeriodSelectorItems(visibleItems),
			all_qtd: visibleItems.filter(
				(i) =>
					(i.type === ActiveStatementTypeEnum.IncomeStatement ||
						i.type === ActiveStatementTypeEnum.Analysis) &&
					i.periodType === PeriodType.Qtd,
			),
			all_ytd: visibleItems.filter(
				(i) =>
					(i.type === ActiveStatementTypeEnum.IncomeStatement ||
						i.type === ActiveStatementTypeEnum.Analysis) &&
					i.periodType === PeriodType.Ytd,
			),
			all_interim: visibleItems.filter(
				(i) =>
					i.type === ActiveStatementTypeEnum.BalanceSheet &&
					i.statementType === StatementType.Interim,
			),
		};

		const tabPresets =
			activeStatementType === ActiveStatementTypeEnum.BalanceSheet ||
			activeStatementType === ActiveStatementTypeEnum.IncomeStatement ||
			activeStatementType === ActiveStatementTypeEnum.Analysis
				? STATEMENT_TAB_CONFIG[activeStatementType].presets
				: [];

		const itemsByYear = visibleItems.reduce<Record<string, PeriodSelectorItem[]>>((acc, item) => {
			const year = DateTime.fromISO(item.date).toFormat("yyyy");
			if (!acc[year]) acc[year] = [];
			acc[year].push(item);
			return acc;
		}, {});

		return {
			presetItems,
			presetOptions: tabPresets.map((preset) => preset.option),
			tabPresets,
			yearGroups: Object.entries(itemsByYear)
				.sort(([a], [b]) => Number(b) - Number(a))
				.map(([yearKey, items]) => ({ yearKey, items })),
		};
	}, [isAdmin, periodSelectorItems, activeStatementType]);

	const activePreset = useMemo<ActivePreset>(() => {
		if (selectedCountForTab === 0) return undefined;
		return presetOptions.find((preset) => {
			const items = presetItems[preset];
			return items.length === selectedCountForTab && items.every(isItemSelected);
		});
	}, [selectedCountForTab, presetOptions, presetItems, isItemSelected]);

	const handlePreset = useCallback(
		(preset: PresetOption) => {
			const itemsToSelect = presetItems[preset];
			if (activeStatementType === ActiveStatementTypeEnum.BalanceSheet) {
				setSelectedBalanceSheetIds(
					new Set(itemsToSelect.filter(isBalanceSheetPeriodSelectorItem).map((i) => i.id)),
				);
			} else if (activeStatementType === ActiveStatementTypeEnum.IncomeStatement) {
				setSelectedIncomeStatementIds(
					new Set(itemsToSelect.filter(isIncomeStatementPeriodSelectorItem).map((i) => i.id)),
				);
			} else if (activeStatementType === ActiveStatementTypeEnum.Analysis) {
				setSelectedFinancialPeriodIds(
					new Set(itemsToSelect.filter(isAnalysisPeriodSelectorItem).map((i) => i.id)),
				);
			}
		},
		[
			activeStatementType,
			presetItems,
			setSelectedBalanceSheetIds,
			setSelectedIncomeStatementIds,
			setSelectedFinancialPeriodIds,
		],
	);

	const toggleItem = useCallback(
		(item: PeriodSelectorItem) => {
			if (isBalanceSheetPeriodSelectorItem(item)) toggleBalanceSheet(item.id);
			else if (isIncomeStatementPeriodSelectorItem(item)) toggleIncomeStatement(item.id);
			else toggleFinancialPeriod(item.id);
		},
		[toggleBalanceSheet, toggleIncomeStatement, toggleFinancialPeriod],
	);

	return (
		<>
			<div className="flex flex-col gap-2.5 px-4 py-3 border-b border-bc-subtle">
				{tableActions.map((action) => (
					<ToggleRow
						key={action.key}
						testId={action.testId}
						label={action.label}
						checked={action.checked}
						onToggle={action.onToggle}
					/>
				))}
				{!hideExpandControls && (
					<div className="flex items-center gap-2">
						<button
							data-testid="collapse-all-button"
							type="button"
							onClick={() => setExpanded([])}
							className="text-sm font-medium text-brand-primary hover:underline"
						>
							Collapse
						</button>
						<span className="text-tertiary text-xs">|</span>
						<button
							data-testid="expand-all-button"
							type="button"
							onClick={() => setExpanded(expandableRowIds)}
							disabled={expandableRowIds.length === 0}
							className="text-sm font-medium text-brand-primary hover:underline disabled:opacity-40 disabled:no-underline"
						>
							Expand
						</button>
						<HotkeyTag testId="expand-all-hotkey" className="ml-auto" hotkey="E" />
					</div>
				)}
			</div>

			<div className="flex gap-2 p-3 border-b border-bc-subtle">
				{tabPresets.map(({ option, label }) => {
					const isActive = activePreset === option;
					const isDisabled = presetItems[option].length === 0;
					return (
						<button
							key={option}
							type="button"
							disabled={isDisabled}
							onClick={() => handlePreset(option)}
							className={cn(
								"px-3 py-1 rounded-full border text-xs font-medium transition-colors",
								isActive
									? "bg-brand-primary border-brand-primary text-inverse"
									: "bg-surface-base border-bc-default text-secondary",
								isDisabled && "opacity-40 cursor-not-allowed",
								!isDisabled && !isActive && "hover:border-brand-primary hover:text-brand-primary",
							)}
						>
							{label}
						</button>
					);
				})}
			</div>

			<div className="flex-1 overflow-y-auto p-3 space-y-3">
				<PendingExtractionsIndicator accountId={accountId} />
				{isAdmin && activeStatementType !== ActiveStatementTypeEnum.Analysis && (
					<button
						type="button"
						onClick={() => setShowCreateModal(true)}
						data-testid="period-add-statement"
						className="flex items-center gap-1 text-sm font-medium text-link hover:text-link-hover transition-colors"
					>
						<Icon type="plus" size="micro" />
						<span>Add Statement</span>
					</button>
				)}
				<PeriodYearList
					groups={yearGroups}
					getItemKey={(item) => item.id}
					testId="period-selector"
					emptyLabel="No statements available"
					isSelected={isItemSelected}
					onToggle={toggleItem}
					renderLabel={(item) => <FinancialsPeriodSelectorLabel item={item} isAdmin={isAdmin} />}
				/>
			</div>

			{showCreateModal && createConfig && (
				<CreateOrUpdatePeriodModal
					accountId={accountId}
					onClose={() => setShowCreateModal(false)}
					createConfig={createConfig}
				/>
			)}
		</>
	);
};
